import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pause, Play, Edit3, MessageSquare, Zap, StopCircle } from "lucide-react";
import Layout from "../components/Layout";
import api, { errMessage } from "../lib/api";

const PLACEHOLDER_HINT = "Placeholders: {address} {city} {state} {asking} {arv} {mao} {profit} {beds} {baths}";

export default function Sequences() {
  const [templates, setTemplates] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [showTmpl, setShowTmpl] = useState(false);
  const [editingTmpl, setEditingTmpl] = useState(null);
  const [showSeq, setShowSeq] = useState(false);
  const [showConv, setShowConv] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, e] = await Promise.all([
        api.get("/sms/templates"),
        api.get("/sms/sequences"),
        api.get("/sms/enrollments"),
      ]);
      setTemplates(t.data || []);
      setSequences(s.data || []);
      setEnrollments(e.data || []);
    } catch (err) {
      toast.error(errMessage(err, "Could not load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveTmpl = async (data) => {
    try {
      if (editingTmpl) {
        await api.patch(`/sms/templates/${editingTmpl.id}`, data);
        toast.success("Template updated");
      } else {
        await api.post("/sms/templates", data);
        toast.success("Template created");
      }
      setShowTmpl(false);
      setEditingTmpl(null);
      load();
    } catch (err) {
      toast.error(errMessage(err, "Save failed"));
    }
  };

  const delTmpl = async (id) => {
    if (!window.confirm("Delete template?")) return;
    try {
      await api.delete(`/sms/templates/${id}`);
      load();
    } catch (err) {
      toast.error(errMessage(err, "Delete failed"));
    }
  };

  const saveSeq = async (data) => {
    try {
      await api.post("/sms/sequences", data);
      toast.success("Sequence created");
      setShowSeq(false);
      load();
    } catch (err) {
      toast.error(errMessage(err, "Save failed"));
    }
  };

  const delSeq = async (id) => {
    if (!window.confirm("Delete sequence? Enrollments keep running until complete.")) return;
    try {
      await api.delete(`/sms/sequences/${id}`);
      load();
    } catch (err) {
      toast.error(errMessage(err, "Delete failed"));
    }
  };

  const pauseEnr = async (id) => {
    await api.post(`/sms/enrollments/${id}/pause`).then(load);
  };
  const resumeEnr = async (id) => {
    try {
      await api.post(`/sms/enrollments/${id}/resume`);
      load();
    } catch (err) {
      toast.error(errMessage(err, "Resume failed"));
    }
  };

  const activeE = enrollments.filter((e) => ["active", "paused"].includes(e.status));
  const completedE = enrollments.filter((e) => ["completed", "opted_out", "error"].includes(e.status));

  return (
    <Layout>
      <div className="px-8 py-10 max-w-6xl">
        <div className="mb-8">
          <div className="overline mb-3">// Follow-up engine</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Sequences.</h1>
          <p className="text-sm text-[--ink-muted] mt-2 max-w-xl">
            Multi-step SMS drips that fire automatically. Replies pause the drip.
            STOP opts the contact out permanently.
          </p>
        </div>

        <div className="flex items-center gap-1 mb-6 border-b border-[--line]">
          {[
            { id: "active", label: `Active (${activeE.length})` },
            { id: "history", label: `History (${completedE.length})` },
            { id: "sequences", label: `Sequences (${sequences.length})` },
            { id: "templates", label: `Templates (${templates.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`seq-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium ${
                tab === t.id
                  ? "border-b-2 border-[--ink] text-[--ink]"
                  : "text-[--ink-muted] hover:text-[--ink]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="text-sm text-[--ink-muted] py-12 text-center">Loading…</div>}

        {!loading && tab === "templates" && (
          <>
            <div className="flex justify-end mb-4">
              <button
                data-testid="new-template"
                onClick={() => { setEditingTmpl(null); setShowTmpl(true); }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={14} strokeWidth={1.5} /> New template
              </button>
            </div>
            {templates.length === 0 ? (
              <Empty icon={MessageSquare} title="No templates" cta="New template" onClick={() => setShowTmpl(true)} />
            ) : (
              <div className="space-y-2" data-testid="templates-list">
                {templates.map((t) => (
                  <div key={t.id} className="surface-card p-4 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-base">{t.name}</div>
                      <div className="text-xs font-mono text-[--ink-muted] whitespace-pre-wrap mt-1">
                        {t.body}
                      </div>
                    </div>
                    <button onClick={() => { setEditingTmpl(t); setShowTmpl(true); }} className="btn-ghost !py-1.5 !px-3 text-xs">
                      <Edit3 size={12} strokeWidth={1.5} />
                    </button>
                    <button data-testid={`del-tmpl-${t.id}`} onClick={() => delTmpl(t.id)} className="btn-ghost !py-1.5 !px-3 text-xs">
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && tab === "sequences" && (
          <>
            <div className="flex justify-end mb-4">
              <button
                data-testid="new-sequence"
                onClick={() => setShowSeq(true)}
                disabled={templates.length === 0}
                className="btn-primary flex items-center gap-2 disabled:opacity-40"
                title={templates.length === 0 ? "Create at least one template first" : ""}
              >
                <Plus size={14} strokeWidth={1.5} /> New sequence
              </button>
            </div>
            {sequences.length === 0 ? (
              <Empty
                icon={Zap}
                title={templates.length === 0 ? "Create a template first" : "No sequences yet"}
                cta="New sequence"
                onClick={() => templates.length > 0 && setShowSeq(true)}
              />
            ) : (
              <div className="space-y-2" data-testid="sequences-list">
                {sequences.map((s) => (
                  <div key={s.id} className="surface-card p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="font-display font-bold text-base">{s.name}</div>
                        <div className="text-xs font-mono text-[--ink-muted] mt-1">
                          {s.steps.length} step{s.steps.length !== 1 ? "s" : ""} ·{" "}
                          {s.steps.map((st) => `D${st.offset_days}`).join(" → ")}
                        </div>
                      </div>
                      <button data-testid={`del-seq-${s.id}`} onClick={() => delSeq(s.id)} className="btn-ghost !py-1.5 !px-3 text-xs">
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && (tab === "active" || tab === "history") && (
          <EnrollmentsList
            enrollments={tab === "active" ? activeE : completedE}
            onPause={pauseEnr}
            onResume={resumeEnr}
            onOpenConversation={(id) => setShowConv(id)}
          />
        )}
      </div>

      {showTmpl && (
        <TemplateModal
          template={editingTmpl}
          onClose={() => { setShowTmpl(false); setEditingTmpl(null); }}
          onSubmit={saveTmpl}
        />
      )}

      {showSeq && (
        <SequenceModal
          templates={templates}
          onClose={() => setShowSeq(false)}
          onSubmit={saveSeq}
        />
      )}

      {showConv && (
        <ConversationModal
          conversationId={showConv}
          onClose={() => setShowConv(null)}
        />
      )}
    </Layout>
  );
}

function EnrollmentsList({ enrollments, onPause, onResume, onOpenConversation }) {
  if (enrollments.length === 0) {
    return (
      <Empty icon={MessageSquare} title="Nothing here yet" cta={null} />
    );
  }
  return (
    <div className="space-y-2" data-testid="enrollments-list">
      {enrollments.map((e) => {
        const due = e.next_send_at ? new Date(e.next_send_at) : null;
        const dueText = due
          ? due < new Date()
            ? "due now"
            : `next: ${due.toLocaleDateString()} ${due.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
          : "—";
        return (
          <div key={e.id} data-testid={`enr-card-${e.id}`} className="surface-card p-4 flex items-center gap-4">
            <StatusDot status={e.status} />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm truncate">
                {e.contact_name || "—"} · <span className="font-mono">{e.contact_phone}</span>
              </div>
              <div className="text-xs text-[--ink-muted] mt-0.5 truncate">
                {e.sequence_name} · step {e.current_step + (e.status === "completed" ? 0 : 1)} of {e.current_step + 1}+ ·{" "}
                {e.property_data?.address || "—"}
              </div>
              <div className="text-[10px] font-mono text-[--ink-muted] mt-0.5 uppercase tracking-wider">
                {e.status} · {dueText}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {e.conversation_id && (
                <button onClick={() => onOpenConversation(e.conversation_id)} className="btn-ghost !py-1.5 !px-3 text-xs" title="View thread">
                  <MessageSquare size={12} strokeWidth={1.5} />
                </button>
              )}
              {e.status === "active" && (
                <button data-testid={`pause-${e.id}`} onClick={() => onPause(e.id)} className="btn-ghost !py-1.5 !px-3 text-xs" title="Pause">
                  <Pause size={12} strokeWidth={1.5} />
                </button>
              )}
              {e.status === "paused" && (
                <button data-testid={`resume-${e.id}`} onClick={() => onResume(e.id)} className="btn-ghost !py-1.5 !px-3 text-xs" title="Resume">
                  <Play size={12} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    active: "bg-green-500",
    paused: "bg-yellow-500",
    completed: "bg-[--ink-muted]",
    opted_out: "bg-red-500",
    error: "bg-red-500",
  };
  return <span className={`w-1.5 h-1.5 shrink-0 ${map[status] || "bg-[--ink-muted]"}`} />;
}

function Empty({ icon: Icon, title, cta, onClick }) {
  return (
    <div className="border border-dashed border-[--line] p-12 text-center">
      <Icon size={32} strokeWidth={1.25} className="mx-auto mb-3 text-[--ink-muted]" />
      <div className="font-display text-lg font-bold mb-1">{title}</div>
      {cta && (
        <button onClick={onClick} className="btn-primary mt-3">
          {cta}
        </button>
      )}
    </div>
  );
}

// ───────────── Template Modal ─────────────
function TemplateModal({ template, onClose, onSubmit }) {
  const [name, setName] = useState(template?.name || "");
  const [body, setBody] = useState(template?.body || "");

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    onSubmit({ name: name.trim(), body });
  };

  return (
    <div data-testid="tmpl-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white max-w-xl w-full border border-[--ink]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[--line]">
          <div className="overline">// {template ? "Edit" : "New"} template</div>
          <h2 className="font-display text-xl font-bold tracking-tighter mt-1">SMS template</h2>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs text-[--ink-muted] block mb-1 font-mono uppercase tracking-wider">Name</span>
            <input data-testid="tmpl-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm" required />
          </label>
          <label className="block">
            <span className="text-xs text-[--ink-muted] block mb-1 font-mono uppercase tracking-wider">Body ({body.length}/320)</span>
            <textarea data-testid="tmpl-body" value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={320} className="w-full text-sm font-mono" required />
            <div className="text-[10px] text-[--ink-muted] mt-1 font-mono">{PLACEHOLDER_HINT}</div>
          </label>
        </div>
        <div className="border-t border-[--line] p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="tmpl-save" className="btn-primary">Save</button>
        </div>
      </form>
    </div>
  );
}

// ───────────── Sequence Modal ─────────────
function SequenceModal({ templates, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [steps, setSteps] = useState([{ offset_days: 0, template_id: templates[0]?.id || "" }]);

  const addStep = () => setSteps([...steps, { offset_days: 3, template_id: templates[0]?.id || "" }]);
  const rmStep = (idx) => setSteps(steps.filter((_, i) => i !== idx));
  const setStep = (idx, k, v) => {
    const next = steps.slice();
    next[idx] = { ...next[idx], [k]: v };
    setSteps(next);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || steps.some((s) => !s.template_id)) {
      toast.error("Name + template required on every step");
      return;
    }
    onSubmit({
      name: name.trim(),
      steps: steps.map((s) => ({
        offset_days: Number(s.offset_days) || 0,
        template_id: s.template_id,
      })),
    });
  };

  return (
    <div data-testid="seq-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white max-w-xl w-full border border-[--ink]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[--line]">
          <div className="overline">// New sequence</div>
          <h2 className="font-display text-xl font-bold tracking-tighter mt-1">Build a drip</h2>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs text-[--ink-muted] block mb-1 font-mono uppercase tracking-wider">Name</span>
            <input data-testid="seq-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm" required placeholder="e.g. Standard 3-touch" />
          </label>
          <div>
            <div className="text-xs text-[--ink-muted] mb-2 font-mono uppercase tracking-wider">Steps</div>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <div key={i} className="flex gap-2 items-center text-sm">
                  <span className="text-xs font-mono text-[--ink-muted] w-6">#{i + 1}</span>
                  <div className="flex items-center gap-1">
                    <input
                      data-testid={`step-days-${i}`}
                      type="number"
                      min="0"
                      value={s.offset_days}
                      onChange={(e) => setStep(i, "offset_days", e.target.value)}
                      className="w-16 text-sm font-mono"
                    />
                    <span className="text-xs text-[--ink-muted]">d</span>
                  </div>
                  <select
                    data-testid={`step-tmpl-${i}`}
                    value={s.template_id}
                    onChange={(e) => setStep(i, "template_id", e.target.value)}
                    className="flex-1 text-sm"
                  >
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {steps.length > 1 && (
                    <button type="button" onClick={() => rmStep(i)} className="btn-ghost !py-1 !px-2 text-xs">
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" data-testid="add-step" onClick={addStep} className="btn-ghost mt-2 text-xs">
              <Plus size={12} strokeWidth={1.5} /> Add step
            </button>
          </div>
        </div>
        <div className="border-t border-[--line] p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="seq-save" className="btn-primary">Save</button>
        </div>
      </form>
    </div>
  );
}

// ───────────── Conversation Modal ─────────────
function ConversationModal({ conversationId, onClose }) {
  const [conv, setConv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/sms/conversations/${conversationId}`)
      .then((r) => setConv(r.data))
      .catch(() => toast.error("Could not load conversation"))
      .finally(() => setLoading(false));
  }, [conversationId]);

  return (
    <div data-testid="conv-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full max-h-[85vh] flex flex-col border border-[--ink]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[--line]">
          <div className="overline">// Thread</div>
          <h2 className="font-display text-lg font-bold tracking-tighter mt-1">
            {conv?.contact_name || conv?.contact_phone || "Conversation"}
          </h2>
          <div className="text-xs font-mono text-[--ink-muted] mt-0.5">
            {conv?.contact_phone}
            {conv?.opted_out && <span className="ml-2 text-red-600">· opted out</span>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 bg-[--bg-soft]">
          {loading && <div className="text-xs text-[--ink-muted] text-center py-6">Loading…</div>}
          {!loading && conv?.messages?.length === 0 && (
            <div className="text-xs text-[--ink-muted] text-center py-6">No messages yet</div>
          )}
          {!loading && conv?.messages?.map((m, i) => (
            <div key={i} className={`mb-3 flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] p-2.5 text-sm ${
                m.direction === "out"
                  ? "bg-[--ink] text-white"
                  : m.status === "opt_out"
                  ? "bg-red-50 border border-red-200 text-red-800"
                  : "bg-white border border-[--line]"
              }`}>
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div className={`text-[10px] font-mono mt-1 ${m.direction === "out" ? "text-white/60" : "text-[--ink-muted]"}`}>
                  {new Date(m.at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-[--line] p-3 flex justify-end">
          <button onClick={onClose} className="btn-ghost">Close</button>
        </div>
      </div>
    </div>
  );
}
